import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import React, { useEffect, useState } from "react";
import { fetchBlobFromUrl } from "../../api/person/person-attachment";
import { SupportAttachment, supportAttachmentUrl } from "../../api/support-tickets";
import ImageZoomDialog from "../common/image-zoom-dialog";

/**
 * One picture inside a thread, streamed through the authenticated download
 * endpoint (the bucket is private, so the URL cannot go straight into src).
 *
 * Clicking it opens it as large as the window allows. A screenshot of a
 * problem is evidence — a red ring around one cell of a table, an error at the
 * foot of a page — and none of that survives being shown 340 pixels wide. The
 * same component draws the reporter's panel and the staff thread, so both
 * sides read the same picture the same way.
 *
 * `context` is what the browser recorded when the capture was taken: the page,
 * the screen, the browser and any console errors from before it. It belongs
 * beside the picture rather than in a column of its own, because it answers
 * the questions the picture provokes — which page is this, what browser, what
 * broke just before. Serialized for staff only, so it is simply absent in the
 * reporter's panel.
 */

/** Splits on http(s) links, keeping them, so each can be made clickable. */
const LINK_PATTERN = /(https?:\/\/\S+)/g;
/** Whether a piece of that split is one of the links. A separate, non-global
 *  pattern: `test` on a /g regex carries lastIndex between calls and would
 *  answer differently for the same string. */
const IS_LINK = /^https?:\/\//;
interface SupportAttachmentImageProps {
	attachment: SupportAttachment;
	context?: string;
}

const SupportAttachmentImage: React.FC<SupportAttachmentImageProps> = ({
	attachment,
	context,
}) => {
	const [imageUrl, setImageUrl] = useState<string | null>(null);
	const [isZoomed, setIsZoomed] = useState(false);

	useEffect(() => {
		let objectUrl: string | null = null;
		fetchBlobFromUrl(supportAttachmentUrl(attachment.id)).then(({ blob }) => {
			objectUrl = URL.createObjectURL(blob);
			setImageUrl(objectUrl);
		});
		return () => {
			if (objectUrl) URL.revokeObjectURL(objectUrl);
		};
	}, [attachment.id]);

	const alt = attachment.caption || attachment.name;

	// The space the picture will take, held from the first paint.
	//
	// The bytes come from an authenticated fetch, so there is nothing in the
	// markup for the browser to size until that resolves — the picture was
	// zero-height until then, and everything under it jumped down when it
	// landed. That is a conversation moving under whoever is reading it, and
	// it was also what defeated the panel's scroll: the growth read as the
	// reader scrolling away.
	//
	// aspectRatio rather than a computed height, so it stays right when the
	// panel is narrower than 340. Null shape — anything uploaded before ODI
	// recorded it — falls back to the picture sizing itself, as before.
	const heldShape =
		attachment.width && attachment.height
			? { aspectRatio: `${attachment.width} / ${attachment.height}` }
			: {};

	return (
		<Box sx={{ mt: 1 }} data-component="support-message-attachment">
			<Box
				sx={{
					maxWidth: "100%",
					width: 340,
					border: 1,
					borderColor: "divider",
					display: "block",
					bgcolor: "action.hover",
					...heldShape,
				}}
			>
				{imageUrl && (
					<ButtonBase
						onClick={() => setIsZoomed(true)}
						data-component="support-attachment-open"
						sx={{
							display: "block",
							width: "100%",
							height: "100%",
							cursor: "zoom-in",
						}}
					>
						<Box
							component="img"
							src={imageUrl}
							alt={alt}
							sx={{
								display: "block",
								width: "100%",
								height: "100%",
								objectFit: "contain",
							}}
						/>
					</ButtonBase>
				)}
			</Box>
			{attachment.caption && (
				// Inherited colour, dimmed — never a fixed one. This renders in
				// two places: the staff thread, on a paper background, and the
				// reporter's own bubble in the panel, which is primary.main
				// with primary.contrastText. text.secondary was near-black
				// there, so a reporter's note sat black on blue. Dimming what
				// the container already set is what the timestamp beside it
				// does, and it is right on both.
				<Typography variant="caption" sx={{ opacity: 0.7 }}>
					{attachment.caption}
				</Typography>
			)}
			<ImageZoomDialog
				open={isZoomed}
				onClose={() => setIsZoomed(false)}
				src={imageUrl ?? ""}
				alt={alt}
				details={
					context ? (
						<>
							<Divider />
							<Box
								component="pre"
								data-component="support-attachment-context"
								sx={{
									// No scroller: it is a handful of lines,
									// and a box that scrolls hides the one
									// line nobody thought to look for.
									m: 0,
									px: 2,
									py: 1.5,
									fontFamily: "monospace",
									fontSize: 11,
									whiteSpace: "pre-wrap",
									color: "text.secondary",
									bgcolor: "background.paper",
								}}
							>
								{/* The page is a link: the first thing anyone
								    answering does is go and look at it. */}
								{context.split(LINK_PATTERN).map((part, index) =>
									IS_LINK.test(part) ? (
										<Link
											key={index}
											href={part}
											target="_blank"
											rel="noopener noreferrer"
										>
											{part}
										</Link>
									) : (
										part
									)
								)}
							</Box>
						</>
					) : null
				}
			/>
		</Box>
	);
};

export default SupportAttachmentImage;
